// src/pages/SearchResults/components/MobileSearchSheet.jsx
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { createSearchParams } from "react-router-dom";
import {
  FaBus,
  FaCalendarAlt,
  FaExchangeAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  useSearchCore,
  toLocalYYYYMMDD,
  getReadableDate,
  PALETTE,
} from "../_core";

export default function MobileSearchSheet() {
  const {
    searchFrom,
    setSearchFrom,
    searchTo,
    setSearchTo,
    searchDate,
    setSearchDate,
    mobileSearchOpen,
    setMobileSearchOpen,
    openMobilePicker,
    setCalOpen,
    navigate,
    location,
    setExpandedBusId,
    releaseAllSelectedSeats,
    swapLocations,
  } = useSearchCore();

  const todayStrLocal = toLocalYYYYMMDD(new Date());
  const tmr = new Date();
  tmr.setDate(tmr.getDate() + 1);
  const tomorrowStrLocal = toLocalYYYYMMDD(tmr);

  // Palette fallbacks so it always renders correctly
  const borderLight = PALETTE?.borderLight || PALETTE?.border || "#E5E7EB";
  const textLight = PALETTE?.textLight || PALETTE?.textSubtle || "#6B7280";
  const textDark = PALETTE?.textDark || PALETTE?.text || "#111827";
  const primaryRed = PALETTE?.primaryRed || PALETTE?.primary || "#D84E55";

  const close = () => setMobileSearchOpen(false);

  const doSearch = async () => {
    if (!searchFrom || !searchTo || !searchDate) {
      toast.error("Please fill all fields before searching");
      return;
    }

    await releaseAllSelectedSeats(true);

    navigate({
      pathname: location.pathname,
      search: `?${createSearchParams({
        from: searchFrom,
        to: searchTo,
        date: searchDate,
      })}`,
    });

    setExpandedBusId(null);
    close();
  };

  useEffect(() => {
    if (!mobileSearchOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileSearchOpen]);

  if (!mobileSearchOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/45 backdrop-blur-[2px] z-[10030]"
        onClick={close}
      />

      {/* Compact top card */}
      <motion.div
        initial={{ y: -18, opacity: 0, scale: 0.985 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: -18, opacity: 0, scale: 0.985 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-[10031] flex justify-center pointer-events-none"
      >
        <div className="mt-2 w-[94%] max-w-[440px] rounded-[24px] bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] border border-gray-200 overflow-hidden pointer-events-auto">
          {/* top handle */}
          <div className="pt-2 pb-1 flex justify-center">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          {/* compact top row */}
          <div className="px-3 pb-2 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-600 px-3 py-1 text-[11px] font-semibold">
              <FaSearch className="text-[10px]" />
              Edit Search
            </div>

            <button
              onClick={close}
              className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <FaTimes className="text-sm text-gray-700" />
            </button>
          </div>

          {/* MOBILE SEARCH CARD */}
          <div className="lg:hidden px-3 pb-3">
            <div
              className="relative rounded-[22px] border overflow-hidden bg-white"
              style={{ borderColor: borderLight }}
            >
              {/* FROM + TO block */}
              <div className="relative px-3 py-2.5">
                {/* FROM */}
                <div
                  className="flex items-center gap-3 py-2.5 border-b"
                  style={{ borderColor: borderLight }}
                >
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <FaBus
                      className="text-sm"
                      style={{ color: textLight }}
                    />
                  </div>

                  <div className="w-full min-w-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      From
                    </label>
                    <button
                      type="button"
                      onClick={() => openMobilePicker("from")}
                      className="w-full text-left pt-1"
                    >
                      <span
                        className={`block text-[15px] leading-tight truncate ${
                          searchFrom
                            ? "font-semibold text-gray-900"
                            : "text-gray-400"
                        }`}
                      >
                        {searchFrom || "Select departure"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* TO */}
                <div className="flex items-center gap-3 pt-3 pb-1">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <FaBus
                      className="text-sm"
                      style={{ color: textLight }}
                    />
                  </div>

                  <div className="w-full min-w-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      To
                    </label>
                    <button
                      type="button"
                      onClick={() => openMobilePicker("to")}
                      className="w-full text-left pt-1"
                    >
                      <span
                        className={`block text-[15px] leading-tight truncate ${
                          searchTo
                            ? "font-semibold text-gray-900"
                            : "text-gray-400"
                        }`}
                      >
                        {searchTo || "Select destination"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* SWAP BUTTON */}
                <div className="absolute top-1/2 right-3 transform -translate-y-1/2 z-10">
                  <motion.button
                    whileTap={{ scale: 0.92, rotate: 180 }}
                    onClick={swapLocations}
                    className="bg-white p-2.5 rounded-full shadow-md transition-all duration-200"
                    style={{ border: `1px solid ${borderLight}` }}
                    title="Swap locations"
                  >
                    <FaExchangeAlt
                      className="text-sm rotate-90"
                      style={{ color: textLight }}
                    />
                  </motion.button>
                </div>
              </div>

              {/* DATE */}
              <div
                className="flex items-center gap-3 px-3 py-3 border-t"
                style={{ borderColor: borderLight }}
              >
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <FaCalendarAlt
                    className="text-sm"
                    style={{ color: textLight }}
                  />
                </div>

                <div
                  onClick={() => setCalOpen(true)}
                  className="flex-grow cursor-pointer min-w-0"
                >
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Date of Journey
                  </label>
                  <span
                    className="block text-[15px] font-semibold leading-tight pt-1 truncate"
                    style={{ color: textDark }}
                  >
                    {getReadableDate(searchDate)}
                  </span>
                </div>

                <div className="flex flex-col gap-1 items-end shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchDate(todayStrLocal);
                    }}
                    className={`min-w-[72px] text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
                      searchDate === todayStrLocal
                        ? "text-red-500 border-red-200 bg-red-50"
                        : "text-gray-600 border-gray-200 bg-white"
                    }`}
                  >
                    Today
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchDate(tomorrowStrLocal);
                    }}
                    className={`min-w-[72px] text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
                      searchDate === tomorrowStrLocal
                        ? "text-red-500 border-red-200 bg-red-50"
                        : "text-gray-600 border-gray-200 bg-white"
                    }`}
                  >
                    Tomorrow
                  </button>
                </div>
              </div>
            </div>

            {/* Search button */}
            <div className="pt-3">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                onClick={doSearch}
                className="font-heading w-full flex items-center justify-center gap-2 text-white font-semibold tracking-wide py-3.5 rounded-full shadow-none"
                style={{ backgroundColor: primaryRed }}
              >
                <FaSearch />
                Search Buses
              </motion.button>
            </div>
          </div>

          {/* Safe area at bottom for iOS notch */}
          <div style={{ height: "env(safe-area-inset-bottom)" }} />
        </div>
      </motion.div>
    </>,
    document.body
  );
}
